// Radar diagnostics for the field nodes' JJY-YGD368ML-18 radar (80 GHz FMCW,
// 0.05-2 m, ±10° beam, about 30 mm range cells). Firmware 0.3.4-rtct.53-dev
// reports every poll (jjy_pv), the sensor's echo list (jjy_pk), the result of
// a blind-zone change (jjy_bz), a capture summary (jjy_fft) and the sensor's
// own settings (radar_cfg). Pure helpers: no React, network or storage.
import { fieldTime } from "utils/fieldFormat";

export const RADAR_FIRMWARE = "0.3.4-rtct.53-dev";
export const RANGE_MAX_MM = 2000;
export const BEAM_HALF_ANGLE_DEG = 10;
export const BLIND_DEFAULT_MM = 40;
export const BLIND_MIN_MM = 15;
export const BLIND_MAX_MM = 1500;

// A finite number, or null (never 0 for a missing value).
export const finite = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const num = finite;
export const formatMm = (value, digits = 0) =>
  `${Number(value).toLocaleString(undefined, { maximumFractionDigits: digits })} mm`;
export const sgtTime = (value) => `${fieldTime(value)} SGT`;
export const capturedAt = (capture) =>
  capture?.captured_at || capture?.received_at;
// Milliseconds since the epoch, or null for a missing or invalid time.
export const timeOf = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
};

// Depth at which the edge of the beam meets the wall of a vertical pipe the
// radar looks down: radius / tan(half-angle). A joint, slot or ledge there can
// return an echo that beats a weak floor echo. Null without a usable diameter.
export function wallContactDepth(diameterMm, halfAngleDeg = BEAM_HALF_ANGLE_DEG) {
  const d = num(diameterMm);
  const a = num(halfAngleDeg);
  if (d === null || d <= 0 || a === null || a <= 0 || a >= 90) return null;
  return d / 2 / Math.tan((a * Math.PI) / 180);
}

// The firmware accepts whole millimetres, 15-1500 (0 only cancels a change
// that is still queued, so the panel never sends it).
export function parseBlindZone(text) {
  const s = String(text ?? "").trim();
  const error = `Enter whole millimetres from ${BLIND_MIN_MM} to ${BLIND_MAX_MM.toLocaleString()}.`;
  if (!/^\d+$/.test(s)) return { error };
  const value = Number(s);
  return value < BLIND_MIN_MM || value > BLIND_MAX_MM ? { error } : { value };
}

// Every poll as the sensor answered it. 1.0 / 2.0 / 3.0 mm are the vendor's
// sentinels; no real echo can be that close (the blind zone is at least 15 mm).
const SENTINELS = [
  [1, "weak echo"],
  [2, "blind zone"],
  [3, "out of range"],
];
export function pollValue(value) {
  const v = num(value);
  if (v === null) return { text: "no value", sentinel: true };
  const hit = SENTINELS.find(([mark]) => Math.abs(v - mark) < 0.05);
  return hit
    ? { text: hit[1], sentinel: true }
    : { text: `${v.toFixed(1)} mm`, sentinel: false };
}

export const RADAR_STATUS = {
  ok: "OK",
  weak_echo: "Weak echo",
  blind_zone: "In the blind zone",
  out_of_range: "Out of range",
  range_gate: "Outside the accepted range",
  timeout: "No reply",
  disagree: "Polls disagree",
  not_read: "Not read",
};
export const statusText = (status) =>
  status ? RADAR_STATUS[status] || String(status).replaceAll("_", " ") : "Not reported";

// How the sensor answered a text command (jjy_pk.r, jjy_bz.r).
export const AT_RESULT = {
  ok: "OK",
  value: "answered",
  syntax: "command not understood",
  no_exist: "command not supported",
  not_allowed: "not allowed",
  bad_value: "value refused",
  other: "an unexpected reply",
  none: "no reply",
};
export const atText = (result) =>
  result ? AT_RESULT[result] || String(result).replaceAll("_", " ") : "no reply";

// The echo list passes the sensor's own range figure through; metres are
// expected (the sensor spans 0.05-2 m), so small values are converted.
export function echoRangeMm(value) {
  const v = num(value);
  if (v === null) return null;
  return Math.abs(v) <= 10 ? v * 1000 : v;
}

// jjy_pk {r, n, mi, p: [[strength, range], ...]} or a capture's echo list
// (pairs, or objects with strength/range) -> echoes ranked strongest first.
export function echoList(input) {
  if (!input || typeof input !== "object") return null;
  const source = Array.isArray(input) ? { p: input } : input;
  const raw = Array.isArray(source.p)
    ? source.p
    : Array.isArray(source.peaks)
      ? source.peaks
      : [];
  const echoes = raw
    .map((entry) => {
      if (Array.isArray(entry))
        return { strength: num(entry[0]), rangeMm: echoRangeMm(entry[1]) };
      if (entry && typeof entry === "object")
        return {
          strength: num(entry.strength ?? entry.v ?? entry.amplitude),
          rangeMm:
            entry.range_mm !== undefined
              ? num(entry.range_mm)
              : echoRangeMm(entry.range ?? entry.r),
        };
      return null;
    })
    .filter((e) => e && e.rangeMm !== null)
    .sort((a, b) => (b.strength ?? -Infinity) - (a.strength ?? -Infinity))
    .map((e, i) => ({ ...e, rank: i + 1 }));
  return {
    result: source.r ?? (echoes.length ? "value" : null),
    count: num(source.n) ?? echoes.length,
    strongestIndex: num(source.mi),
    echoes,
  };
}

// A capture's amplitudes are one float per bin in the sensor's units (linear
// magnitude expected, scale unconfirmed). In dB relative to the strongest bin:
// 20·log10(a / max). A bin at or below zero, or not a number, has no level.
export function amplitudeToDb(values) {
  const list = Array.isArray(values) ? values : [];
  const max = list.reduce((top, v) => {
    const n = num(v);
    return n !== null && n > top ? n : top;
  }, 0);
  let dropped = 0;
  const db = list.map((v) => {
    const n = num(v);
    if (n === null || n <= 0 || max <= 0) {
      dropped += 1;
      return null;
    }
    return 20 * Math.log10(n / max);
  });
  return { db, dropped };
}

// The capture's range axis starts at the radar's own zero; the node reports
// that range minus the calibration offset (air_mm = range - cal_mm). Shifting
// by the offset puts the profile on the same scale as reported distances.
// Chart points within the sensor's span, nearest first, plus the number of
// bins without a level.
export function captureProfile(capture, offsetMm = 0, maxMm = RANGE_MAX_MM) {
  const ranges = Array.isArray(capture?.range_mm) ? capture.range_mm : [];
  const { db, dropped } = amplitudeToDb(capture?.amplitude);
  const shift = num(offsetMm) ?? 0;
  const points = [];
  for (let i = 0; i < Math.min(ranges.length, db.length); i++) {
    const r = num(ranges[i]);
    if (r === null || db[i] === null) continue;
    const x = r - shift;
    if (x >= 0 && x <= maxMm) points.push({ r: x, db: db[i] });
  }
  return { points: points.sort((a, b) => a.r - b.r), dropped };
}

// Level of a profile at any range, by linear interpolation between cells.
export function levelAt(points, r) {
  if (!points?.length || !Number.isFinite(r)) return null;
  if (r < points[0].r || r > points[points.length - 1].r) return null;
  const i = points.findIndex((p) => p.r >= r);
  if (points[i].r === r || i === 0) return points[i].db;
  const a = points[i - 1];
  const b = points[i];
  return a.db + ((b.db - a.db) * (r - a.r)) / (b.r - a.r);
}

// Adds an earlier capture to the selected one's rows (`cmp`), resampled onto
// the same ranges so one tooltip reads both.
export function withComparison(points, other) {
  if (!other?.length) return points;
  return points.map((p) => ({ ...p, cmp: levelAt(other, p.r) }));
}

// Y-axis bounds on round 10 dB steps (0 dB, the strongest bin, on top).
export function levelDomain(values) {
  const v = values.filter((x) => Number.isFinite(x));
  if (!v.length) return ["auto", "auto"];
  // "+ 0" turns -0 into 0 for the tick labels.
  return [
    Math.floor((Math.min(...v) - 2) / 10) * 10 + 0,
    Math.ceil(Math.max(...v) / 10) * 10 + 0,
  ];
}

// Even ticks for those bounds: every 10 dB, or 20 dB over a wide span.
export function levelTicks(domain) {
  const [lo, hi] = Array.isArray(domain) ? domain : [];
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return undefined;
  const step = hi - lo > 80 ? 20 : 10;
  const ticks = [];
  for (let v = hi; v >= lo; v -= step) ticks.unshift(v + 0);
  return ticks;
}

// The strongest echo beyond the blind zone, the highest level within
// ±halfWidthMm of the expected floor, and the median level beyond the blind
// zone (a rough noise reference), so the floor can be judged at a glance.
export function floorCheck(points, floorMm, blindMm = 0, halfWidthMm = 90) {
  const beyond = (points || []).filter((p) => p.r > (num(blindMm) ?? 0));
  if (!beyond.length) return null;
  const top = (rows) => rows.reduce((best, p) => (p.db > best.db ? p : best));
  const sorted = beyond.map((p) => p.db).sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  const floor = num(floorMm);
  const band =
    floor !== null && floor > 0
      ? beyond.filter((p) => Math.abs(p.r - floor) <= halfWidthMm)
      : [];
  return {
    strongest: top(beyond),
    median,
    near: band.length ? top(band) : null,
  };
}

// True from 0.3.4-rtct.53, false before it, null when the version is unknown
// or in another format (the panel then does not block anything).
export function radarFirmwareSupport(fw) {
  const m = /^(\d+)\.(\d+)\.(\d+)-rtct\.(\d+)/.exec(String(fw ?? "").trim());
  if (!m) return null;
  const have = m.slice(1).map(Number);
  const need = [0, 3, 4, 53];
  for (let i = 0; i < need.length; i++)
    if (have[i] !== need[i]) return have[i] > need[i];
  return true;
}

// Newest telemetry sample whose raw payload carries `key`.
export function newestWith(samples, key) {
  let best = null;
  for (const s of samples || []) {
    const value = s?.raw?.[key];
    const at = timeOf(s?.timestamp);
    if (value === undefined || value === null || at === null) continue;
    if (!best || at > best.at) best = { at, t: s.timestamp, value, sample: s };
  }
  return best;
}

// The newer of two {t, value} records (either may be missing).
const present = (record) =>
  !!record && record.value !== undefined && record.value !== null;
export function newer(a, b) {
  if (!present(a)) return present(b) ? b : null;
  if (!present(b)) return a;
  return (timeOf(b.t) ?? -Infinity) > (timeOf(a.t) ?? -Infinity) ? b : a;
}

const sampleDistance = (s) => {
  if (!s || s.radar_valid === false) return null;
  const mm = num(s.raw?.distance_mm);
  if (mm !== null) return mm;
  const cm = num(s.distance_cm);
  return cm === null ? null : cm * 10;
};

// A telemetry sample in the radar endpoint's `latest` shape.
export function readingFromSample(sample) {
  if (!sample) return null;
  return {
    t: sample.timestamp,
    distance_mm: sampleDistance(sample),
    status: sample.raw?.jjy_status ?? null,
    polls: Array.isArray(sample.raw?.jjy_pv) ? sample.raw.jjy_pv : null,
  };
}

// The reading taken on a capture's wake: it carries the jjy_fft summary and
// precedes the capture message by seconds. Falls back to the nearest reading
// at or before the capture. Within 15 minutes only.
export function captureReading(samples, capturedTime, windowMs = 15 * 60000) {
  const at = timeOf(capturedTime);
  if (at === null) return null;
  let tagged = null;
  let before = null;
  for (const s of samples || []) {
    const t = timeOf(s?.timestamp);
    if (t === null || Math.abs(t - at) > windowMs) continue;
    if (s.raw?.jjy_fft && (!tagged || Math.abs(t - at) < Math.abs(tagged.at - at)))
      tagged = { at: t, s };
    if (t <= at + 60000 && (!before || t > before.at)) before = { at: t, s };
  }
  const hit = tagged || before;
  return hit ? readingFromSample(hit.s) : null;
}

// One radar command (radar_blind_mm or radar_raw) as this browser sent it,
// kept up to date from the page's settings state: the settings endpoint
// describes only the newest request, so its status applies only while
// request_id matches. A newer request for the same key replaces ours on
// screen; a newer request for something else is noted (`followedAt`),
// because a retained command the node has not picked up yet is replaced.
export function trackRequest(local, settings, key) {
  const desired = settings?.desired;
  const server =
    settings?.request_id &&
    desired &&
    Object.prototype.hasOwnProperty.call(desired, key)
      ? {
          key,
          value: desired[key],
          request_id: settings.request_id,
          status: settings.status,
          since: settings.updated_at || null,
          // The endpoint gives only the request's last update, which the
          // node's later reports move on: not when it was asked.
          updated: true,
          reason: settings.reason || "",
        }
      : null;
  if (!local) return server;
  if (server && server.request_id === local.request_id)
    return { ...local, status: server.status, reason: server.reason };
  const later =
    settings?.request_id &&
    settings.request_id !== local.request_id &&
    (timeOf(settings.updated_at) ?? -Infinity) > (timeOf(local.since) ?? Infinity);
  if (later && server) return server;
  return later ? { ...local, followedAt: settings.updated_at } : local;
}

// The radio a report went out on (firmware: radio = "lte" | "wifi").
export function onCellular(raw) {
  const radio = String(raw?.radio ?? raw?.net ?? "").toLowerCase();
  return ["lte", "cellular", "nbiot", "nb-iot", "cat-m"].includes(radio);
}

// radar_stream is a bitmask of what the node streams while it is in live mode:
// 1 the spectrum, 2 the beat signal (ADC samples), 4 the echo list, 8 the
// radar's settings (once). radar_stream_s is the cadence in seconds.
export const STREAM_PARTS = [
  [1, "spectrum"],
  [2, "beat signal"],
  [4, "echo list"],
  [8, "settings dump"],
];
export function streamMaskText(mask) {
  const m = num(mask);
  const parts =
    m !== null && Number.isInteger(m) && m > 0
      ? STREAM_PARTS.filter(([bit]) => m & bit).map(([, name]) => name)
      : [];
  return parts.length ? parts.join(" + ") : "nothing";
}

// 1st, 2nd, 3rd, 4th, 11th, 12th, 13th, 21st...
export function ordinal(n) {
  const v = Math.abs(Math.round(Number(n) || 0));
  const tens = v % 100;
  const suffix = tens >= 11 && tens <= 13 ? "th" : ["th", "st", "nd", "rd"][v % 10] || "th";
  return `${v.toLocaleString()}${suffix}`;
}

// radar_raw_alt (firmware .56): captures take turns between the spectrum
// (mode 1) and the beat signal (mode 2), in streams and bursts alike.
const altText = (alt) => (alt ? "alternating spectrum and beat signal" : "not alternating");

// Rich wakes (firmware .56): radar_burst has radar_stream's bits (8 = the
// settings dump, once each time it is turned on; 0 = off), radar_burst_n
// captures per burst, radar_burst_gap_s seconds between them, a burst on every
// radar_burst_every-th wake that uploads and, from firmware .57, only while
// the solar current is at least radar_burst_min_si mA (0: whatever the sun).
// In words, leaving out what is not given: "spectrum + beat signal, 5
// captures 2 s apart, every 4th upload, only with at least 50 mA of solar".
export function burstSettingsText({ mask = null, n = null, gap = null, every = null, mode = null, alt = null, minSi = null } = {}) {
  const m = num(mask);
  const count = num(n);
  const pause = num(gap);
  const nth = num(every);
  const raw = num(mode);
  const turns = num(alt);
  const sun = num(minSi);
  if (m === 0) return "off";
  return [
    m !== null ? streamMaskText(m) : null,
    count !== null
      ? `${count} capture${count === 1 ? "" : "s"}${count > 1 && pause !== null ? (pause ? ` ${pause} s apart` : " back to back") : ""}`
      : pause !== null
        ? `${pause} s between captures`
        : null,
    nth !== null ? (nth === 1 ? "every upload" : `every ${ordinal(nth)} upload`) : null,
    sun !== null && sun > 0 ? `only with at least ${sun} mA of solar` : null,
    raw !== null ? `raw output mode ${raw}` : null,
    turns !== null ? altText(turns) : null,
  ]
    .filter(Boolean)
    .join(", ");
}
// Rich readings (firmware .58): radar_rich is what the node captures on every
// normal wake (1 the spectrum, 2 the beat signal; 0 off), radar_rich_up what it
// captures as well on a wake that uploads (a part asked for on both is taken
// once), radar_rich_min_bv the battery floor in mV for rich readings and bursts;
// firmware .59's radar_rich_every takes radar_rich on every Nth wake only (1-12;
// the upload wakes' extra parts are taken on every upload wake all the same).
const RICH_PART_NAMES = [
  [2, "beat signal"],
  [1, "spectrum"],
];
// "beat signal and spectrum", "beat signal", "spectrum" or "nothing".
export function richPartsText(mask) {
  const m = num(mask);
  const names =
    m !== null && Number.isInteger(m)
      ? RICH_PART_NAMES.filter(([bit]) => m & bit).map(([, name]) => name)
      : [];
  return names.length ? names.join(" and ") : "nothing";
}
// "every wake", "every 3rd wake".
export const wakeEveryText = (every) => {
  const n = num(every);
  return n !== null && n > 1 ? `every ${ordinal(n)} wake` : "every wake";
};
// In words, leaving out what is not given: "beat signal and spectrum on every
// wake, nothing extra on upload wakes, battery floor 3,200 mV" ("on every 3rd
// wake" with radar_rich_every 3); "off" when nothing is captured.
export function richSettingsText({ mask = null, up = null, minBv = null, every = null } = {}) {
  const m = num(mask);
  const u = num(up);
  const floor = num(minBv);
  if (m === 0 && !(u > 0)) return "off";
  const extra = m !== null && u !== null && Number.isInteger(m) && Number.isInteger(u) ? u & ~m & 3 : null;
  return [
    m !== null
      ? m > 0
        ? `${richPartsText(m)} on ${wakeEveryText(every)}`
        : "nothing on normal wakes"
      : m === null && num(every) !== null
        ? `a reading on ${wakeEveryText(every)}`
        : null,
    extra !== null
      ? extra
        ? `${richPartsText(extra)}${m > 0 ? " as well" : ""} on upload wakes`
        : "nothing extra on upload wakes"
      : u !== null && u > 0
        ? `${richPartsText(u)} on upload wakes`
        : null,
    floor !== null ? `battery floor ${floor.toLocaleString()} mV` : null,
  ]
    .filter(Boolean)
    .join(", ");
}
// The node's own estimate of each beat signal (radar_edge): 0 off; firmware
// .61's peak (1, 2 with a timing benchmark); firmware .62's estimator v2, the
// leading edge and the strongest echoes in its gate (3, 4 with its
// benchmark). It searches radar_gate_min_mm to radar_gate_max_mm; firmware
// .62's hybrid upload (radar_keep_every, radar_keep_mm, radar_keep_db, with
// radar_edge 3 or 4) sends a rich wake's raw captures on every Nth rich wake
// and when the estimate moved or a runner-up echo stands close.
export const EDGE_NAMES = {
  0: "off",
  1: "peak (v1)",
  2: "peak (v1) with its timing benchmark",
  3: "leading edge (v2)",
  4: "leading edge (v2) with its timing benchmark",
};
// "every rich wake"; "every 6th rich wake, and on a move over 20 mm, and when the runner-up echo is within 3 dB";
// "" when none is given.
export function keepRuleText({ every = null, mm = null, db = null } = {}) {
  const n = num(every);
  const move = num(mm);
  const near = num(db);
  return [
    n !== null ? (n > 1 ? `every ${ordinal(n)} rich wake` : "every rich wake") : null,
    move !== null && move > 0 ? `on a move over ${move.toLocaleString()} mm` : null,
    near !== null && near > 0 ? `when the runner-up echo is within ${near.toLocaleString()} dB` : null,
  ]
    .filter(Boolean)
    .join(", and ");
}
// In words, leaving out what is not given: "leading edge (v2), gate 270–1,208 mm, raw captures on every 6th rich wake,
// and on a move over 20 mm"; "off" for 0.
export function edgeSettingsText({ edge = null, gateMin = null, gateMax = null, every = null, mm = null, db = null } = {}) {
  const e = num(edge);
  const lo = num(gateMin);
  const hi = num(gateMax);
  const keep = keepRuleText({ every, mm, db });
  return [
    e !== null ? EDGE_NAMES[e] || `radar_edge ${e}` : null,
    lo !== null && hi !== null
      ? `gate ${lo.toLocaleString()}–${hi.toLocaleString()} mm`
      : lo !== null
        ? `gate from ${lo.toLocaleString()} mm`
        : hi !== null
          ? `gate to ${hi.toLocaleString()} mm`
          : null,
    keep ? `raw captures on ${keep}` : null,
  ]
    .filter(Boolean)
    .join(", ");
}
const EDGE_REQUEST_KEYS = [
  "radar_edge",
  "radar_gate_min_mm",
  "radar_gate_max_mm",
  "radar_keep_every",
  "radar_keep_mm",
  "radar_keep_db",
];
const RICH_REQUEST_KEYS = ["radar_rich", "radar_rich_up", "radar_rich_min_bv", "radar_rich_every"];
const BURST_REQUEST_KEYS = [
  "radar_burst",
  "radar_burst_n",
  "radar_burst_gap_s",
  "radar_burst_every",
  "radar_burst_min_si",
];

// What a settings request asked for, in words.
export function describeRequest(request) {
  const desired = request?.desired || {};
  const key = request?.key;
  if (key === "radar_blind_mm" || "radar_blind_mm" in desired) {
    const value = request.value ?? desired.radar_blind_mm;
    return Number(value) === 0
      ? "cancel of a queued blind-zone change"
      : `blind-zone change to ${formatMm(value)}`;
  }
  if (key === "radar_raw" || "radar_raw" in desired)
    return Number(request.value ?? desired.radar_raw) === 0
      ? "cancel of a queued capture"
      : "echo-profile capture";
  // radar_baud (firmware .60): the radar's serial speed.
  if (key === "radar_baud" || "radar_baud" in desired) {
    const baud = num(key === "radar_baud" && request.value !== undefined ? request.value : desired.radar_baud);
    return baud !== null ? `radar link change to ${baud.toLocaleString()} baud` : "radar link change";
  }
  // radar_raw_mode (firmware .55): the value in AT+OUTRAWDATA=<mode>.
  const mode = num(desired.radar_raw_mode ?? (key === "radar_raw_mode" ? request.value : null));
  const alt = num(desired.radar_raw_alt ?? (key === "radar_raw_alt" ? request.value : null));
  const altPart = alt !== null ? `, ${altText(alt)}` : "";
  if (key === "radar_stream" || "radar_stream" in desired) {
    const value = Number((key === "radar_stream" ? request.value : null) ?? desired.radar_stream);
    if (value === 0) return "radar stream stop";
    if (value === 8) return `radar settings dump${mode !== null ? `, raw output mode ${mode}` : ""}${altPart}`;
    const seconds = num(desired.radar_stream_s);
    return `radar stream (${streamMaskText(value)}${seconds ? ` every ${seconds} s` : ""}${mode !== null ? `, raw output mode ${mode}` : ""}${altPart})`;
  }
  // Rich readings, with the classic bursts turned off in the same command
  // when asked.
  if (RICH_REQUEST_KEYS.some((k) => key === k || k in desired)) {
    const pick = (k) => (key === k && request.value !== undefined ? request.value : desired[k]) ?? null;
    const words = richSettingsText({
      mask: pick("radar_rich"),
      up: pick("radar_rich_up"),
      minBv: pick("radar_rich_min_bv"),
      every: pick("radar_rich_every"),
    });
    const bursts = "radar_burst" in desired && num(desired.radar_burst) === 0 ? ", classic bursts off" : "";
    return words === "off" ? `rich readings off${bursts}` : `rich readings (${words || "settings"})${bursts}`;
  }
  if (BURST_REQUEST_KEYS.some((k) => key === k || k in desired)) {
    const pick = (k) => (key === k && request.value !== undefined ? request.value : desired[k]);
    const value = num(pick("radar_burst"));
    if (value === 0) return "rich-wake bursts off";
    if (value === 8) return `radar settings dump with the next burst${mode !== null ? `, raw output mode ${mode}` : ""}${altPart}`;
    const words = burstSettingsText({
      mask: value,
      n: pick("radar_burst_n"),
      gap: pick("radar_burst_gap_s"),
      every: pick("radar_burst_every"),
      mode,
      alt,
      minSi: pick("radar_burst_min_si"),
    });
    return `rich-wake bursts (${words || "whatever the sun"})`;
  }
  // The node's own estimate, its gate and the hybrid upload (firmware .61-.62).
  if (EDGE_REQUEST_KEYS.some((k) => key === k || k in desired)) {
    const pick = (k) => (key === k && request.value !== undefined ? request.value : desired[k]) ?? null;
    const words = edgeSettingsText({
      edge: pick("radar_edge"),
      gateMin: pick("radar_gate_min_mm"),
      gateMax: pick("radar_gate_max_mm"),
      every: pick("radar_keep_every"),
      mm: pick("radar_keep_mm"),
      db: pick("radar_keep_db"),
    });
    return num(pick("radar_edge")) === 0 && words === "off" ? "on-node estimate off" : `on-node estimate (${words || "settings"})`;
  }
  if (mode !== null) return `raw output mode change to ${mode}${altPart}`;
  if (alt !== null) return alt ? "alternating spectrum and beat signal" : "alternation of spectrum and beat signal off";
  if ("live_s" in desired)
    return Number(desired.live_s) > 0 ? "live-mode start" : "live-mode stop";
  const keys = Object.keys(desired);
  return keys.length ? `settings change (${keys.join(", ")})` : "settings change";
}

// Why a radar command was not sent. The settings endpoint answers 503 when the
// broker did not take a command, and refuses keys it does not know (a backend
// without radar support).
export const radarCommandError = (e) =>
  e?.status === 503
    ? "The server could not pass the command to the broker, so it did not reach the node. Try again."
    : /supported settings/i.test(e?.message || "")
      ? "This server does not accept radar commands yet; it needs the updated backend."
      : e?.message || "The command could not be sent.";

// A request the node received but did not apply: the backend reports it
// "not_applied" when the node's read-back shows other values (it switched
// streaming or bursts off by itself after captures that failed, say). A
// backend without that status leaves such a request "acknowledged"; one
// acknowledged over 10 minutes ago while the node reports other values for
// its persistent keys counts as not applied too. One-shot commands
// (radar_raw, radar_blind_mm) are never compared, since their read-back
// differs by design; nor is the settings-dump bit (8) of radar_stream and
// radar_burst, which the node sends once; nor radar_baud (firmware .60),
// which the node switches on its next wake and the server judges by the
// switch's outcome, so an older rate in the read-back is no sign.
export const NOT_APPLIED_AFTER_MS = 10 * 60 * 1000;
const ONE_SHOT_KEYS = ["radar_raw", "radar_blind_mm"];
const OUTCOME_KEYS = ["radar_baud"];
const ONCE_BIT_KEYS = ["radar_stream", "radar_burst"];
function sameSetting(key, asked, reported) {
  const a = num(asked);
  const b = num(reported);
  if (a !== null && b !== null)
    return ONCE_BIT_KEYS.includes(key) ? (a & ~8) === (b & ~8) : Math.abs(a - b) < 1e-9;
  return String(asked) === String(reported);
}
export function notApplied(request, settings, now = Date.now()) {
  if (!request) return false;
  if (request.status === "not_applied") return true;
  if (request.status !== "acknowledged") return false;
  const since = timeOf(request.since);
  if (since === null || now - since < NOT_APPLIED_AFTER_MS) return false;
  const reported = settings?.reported || {};
  const oneShot = Array.isArray(settings?.one_shot_keys) ? settings.one_shot_keys : ONE_SHOT_KEYS;
  return Object.entries(request.desired || {}).some(
    ([key, value]) =>
      !oneShot.includes(key) &&
      !OUTCOME_KEYS.includes(key) &&
      reported[key] !== undefined &&
      reported[key] !== null &&
      !sameSetting(key, value, reported[key]),
  );
}
// A request the node has not settled: on its way (queued, sent) or received
// and not yet confirmed (acknowledged), unless it was not applied.
export const ASKED_STATUSES = ["queued", "sent", "acknowledged"];
export const stillAsked = (request, settings, now = Date.now()) =>
  !!request && ASKED_STATUSES.includes(request.status) && !notApplied(request, settings, now);
// The status to show for a request: "not_applied" also for an old
// acknowledged one the node did not apply.
export const shownStatus = (request, settings, now = Date.now()) =>
  notApplied(request, settings, now) ? "not_applied" : request?.status;

// Why the node switched streaming or bursts off by itself, from the reading
// that reported it (jjy_stream_err {m, n, r, seq}, jjy_burst_err {m, n, r,
// id}), worded as the backend words its not_applied reason: "switched
// streaming off by itself after 3 failed captures in a row (no_packet, at
// capture 3)"; r "reset" when a reset cut the captures short.
export function switchOffText(err, what = "streaming") {
  if (!err || typeof err !== "object") return "";
  const n = num(err.n);
  const why = typeof err.r === "string" ? err.r.trim() : "";
  const seq = num(err.seq);
  if (why === "reset") return `switched ${what} off by itself after a reset in the middle of its captures (reset)`;
  const details = [why || null, seq !== null && Number.isInteger(seq) ? `at capture ${seq}` : null].filter(Boolean);
  return `switched ${what} off by itself${n !== null && Number.isInteger(n) ? ` after ${n} failed capture${n === 1 ? "" : "s"} in a row` : ""}${details.length ? ` (${details.join(", ")})` : ""}`;
}
// The newest switch-off record (`key`: jjy_stream_err or jjy_burst_err) in
// the telemetry, reported at or after `afterMs` (a minute's grace): {t,
// text}, or null.
export function switchOff(samples, key, afterMs = -Infinity) {
  const hit = newestWith(samples, key);
  if (!hit || typeof hit.value !== "object" || hit.at < afterMs - 60000) return null;
  const text = switchOffText(hit.value, key === "jjy_burst_err" ? "bursts" : "streaming");
  return text ? { t: hit.t, text } : null;
}

// The node's retained config holds only the newest command, so a new one
// waits until the newest request (this browser's or anyone's) has at least
// been acknowledged. Returns that request while it is still on its way: a
// request that is acknowledged, confirmed, not applied, superseded, rejected
// or failed to publish is not.
export function commandInFlight(settings, ...tracked) {
  const server = settings?.request_id
    ? {
        request_id: settings.request_id,
        status: settings.status,
        since: settings.updated_at,
        desired: settings.desired || {},
      }
    : null;
  const newest = [server, ...tracked]
    .filter(Boolean)
    .reduce(
      (best, r) =>
        !best || (timeOf(r.since) ?? -Infinity) > (timeOf(best.since) ?? -Infinity)
          ? r
          : best,
      null,
    );
  return newest && ["queued", "sent"].includes(newest.status) ? newest : null;
}

// A live-mode command still on its way, in commandInFlight's shape, else
// null. The settings endpoint leaves live commands out, but the node keeps
// only the newest command of either kind, so radar commands wait for it too.
export function pendingLiveCommand(live) {
  const r = live?.request;
  if (!r || !["queued", "sent"].includes(r.status)) return null;
  return {
    request_id: r.request_id,
    status: r.status,
    since: r.created_at,
    desired: { ...(r.values || {}), live_s: r.action === "start" ? r.values?.live_s || 1 : 0 },
  };
}
